/*  blackout-cascade.js — one owner for "is this option's gear blacked out".
    Added 22-09-2026 (dev-build 183).

    ── WHY THIS IS ITS OWN FILE ────────────────────────────────────────────────────
    Three surfaces need the same answer: the cart marking a line, the product page
    greying an option, the collection card deciding whether a package is affected.
    None of the three files they live in is loaded on the other two pages, so the
    tempting move is to copy the function into each. That is how two surfaces start
    disagreeing about the same booking — one gets a fix, the others do not, and the
    symptom is a cart that refuses an item the product page said was fine.

    So: one file, loaded by each of the three sections, and the rule exists once.

    ── WHAT IT READS ───────────────────────────────────────────────────────────────
    Both arrays are published by pb-cart.liquid, pp-main.liquid and
    collection-split.liquid (dev-build 182). This file assumes neither exists and
    degrades to "nothing is blocked" — the correct failure direction for a page that
    has lost its data, because the cart gate refuses on a POSITIVE mark and inventing
    marks would refuse real bookings.

      window.csOverrides   the blackout records: effect, first_night, last_night,
                           products[], variants[], locations[], padding
      window.csMappings    option value -> component product, with a rule

    ── THE RULE IS NOT SYMMETRICAL ─────────────────────────────────────────────────
      block_if_any_out   one blocked component is enough. Two different things
                         bundled into one option — a handheld AND a headset mic.
      block_if_all_out   every component must be blocked. One thing named with
                         several models that could serve it — any of three
                         generators. Ops send whichever is free, so the option only
                         dies when they are all out.

    Defaulting a missing rule to `any` is deliberate: it refuses more, and refusing a
    booking we could have taken is recoverable where taking one we cannot fulfil is
    not.                                                                            */
(function () {
  'use strict';

  /*  Dates are compared as YYYY-MM-DD strings, never as Date objects.
      `new Date('2026-09-29')` is midnight UTC, which is the 29th at 10am in Sydney
      and the 28th at 5pm in Los Angeles — so a date-only value put through Date
      arithmetic lands on the wrong day for half the world. String comparison on a
      zero-padded ISO date is exact, ordered, and has no timezone at all.

      The booking bar already learned this the hard way; toNoonUTC exists there for
      the same reason. Nothing here needs a Date, so nothing here makes one.        */
  /*  Ids, not gids. The three surfaces do not agree on the form: the cart builds
      "gid://shopify/Product/123" from a bare numeric attribute, pp-product.js assigns a
      normalised location gid in one place and a raw one in another, and the collection
      card reads whatever Liquid rendered. Every predicate this file replaces already
      compared on the trailing id for exactly that reason; keeping that behaviour is
      preserving what shipped, not loosening it.

      Matching strictly instead would have failed on one surface and not the others,
      which is the bug nobody can reproduce.                                          */
  function idOf(gid) {
    if (gid === 0) return '0';
    if (!gid) return '';
    var s = String(gid);
    var i = s.lastIndexOf('/');
    return i === -1 ? s : s.slice(i + 1);
  }

  function shiftDays(iso, days) {
    if (!iso || !days) return iso || '';
    var p = String(iso).slice(0, 10).split('-');
    if (p.length !== 3) return iso;
    /*  Date.UTC for the arithmetic only, then straight back to a string. The value
        never leaves this function as a Date. */
    var t = Date.UTC(+p[0], +p[1] - 1, +p[2]) + days * 86400000;
    var d = new Date(t);
    var m = String(d.getUTCMonth() + 1).padStart(2, '0');
    var day = String(d.getUTCDate()).padStart(2, '0');
    return d.getUTCFullYear() + '-' + m + '-' + day;
  }

  function overlaps(aFrom, aTo, bFrom, bTo) {
    if (!aFrom || !aTo || !bFrom || !bTo) return false;
    return aFrom <= bTo && bFrom <= aTo;
  }

  /*  A hire is tested by its NIGHTS, not its endpoints — the fix for the Thursday
      problem. A hire returning on the morning the blackout starts used the night
      BEFORE, so it does not touch a blacked-out night; a hire leaving on the last
      day uses the night after. Both are clear unless the record's own switch says
      otherwise.

      pickUp / dropOff are the customer's dates. The nights they occupy run from
      pickUp to the day before dropOff.                                            */
  function hireHitsRecord(pickUp, dropOff, rec) {
    if (!pickUp || !dropOff) return false;
    var first = rec.first_night, last = rec.last_night;
    if (!first || !last) return false;

    /*  dev-build 191: no padding. lead_nights / turnaround_nights were retired from
        item_blackout on 25-09-2026 and are no longer published. The record's nights ARE
        the blackout.                                                                  */

    var lastNight = shiftDays(dropOff, -1);
    if (lastNight < pickUp) lastNight = pickUp;

    if (overlaps(pickUp, lastNight, first, last)) return true;

    /*  The two edges, off by default. Neither uses a blacked-out night, which is why
        neither is caught above and why each has to be asked per record — the answer
        is a property of the job that caused the blackout, not of the gear. */
    if (rec.block_return_on_start_day === true && dropOff === first) return true;
    /*  dev-build 193. The morning AFTER the last night — the day the gear lands back.
        This compared against `last` itself, a day the overlap test above has already
        blocked, so the switch never changed anything. Admin wording agrees: "Pick up
        from (day after)".                                                            */
    if (rec.block_pickup_on_end_day === true && pickUp === shiftDays(last, 1)) return true;

    return false;
  }

  function locationApplies(rec, locationGid) {
    var locs = rec.locations;
    /*  Empty means everywhere. The Liquid side had this backwards for months because
        it tested `.size == 0` on a nil; here an absent array and an empty one are
        both falsy-length, so the one test covers both. */
    if (!Array.isArray(locs) || locs.length === 0) return true;
    if (!locationGid) return false;
    var want = idOf(locationGid);
    for (var i = 0; i < locs.length; i++) if (idOf(locs[i]) === want) return true;
    return false;
  }

  /*  Every product blocked for this hire, at this location, as a Set of product GIDs.
      Computed ONCE per page and handed to the per-option checks, rather than per
      option — 40 mappings against 4 records per variant is a nested loop inside a
      render, and it is the same answer every time.                                 */
  function blockedProductSet(pickUp, dropOff, locationGid) {
    var out = Object.create(null);
    var recs = window.csOverrides;
    if (!Array.isArray(recs)) return out;

    for (var i = 0; i < recs.length; i++) {
      var r = recs[i];
      if (!r || r.effect !== 'block') continue;
      if (!locationApplies(r, locationGid)) continue;
      if (!hireHitsRecord(pickUp, dropOff, r)) continue;

      if (Array.isArray(r.products)) {
        for (var p = 0; p < r.products.length; p++) out[idOf(r.products[p])] = true;
      }
      /*  A named VARIANT does not put its product in this set. The set means "this
          gear is unavailable", and one dead configuration of a product does not make
          the product unavailable — the other variants still book. Variant-level
          blocking is answered by blockedVariants below, separately and on purpose. */
    }
    return out;
  }

  function blockedVariantSet(pickUp, dropOff, locationGid) {
    var out = Object.create(null);
    var recs = window.csOverrides;
    if (!Array.isArray(recs)) return out;
    for (var i = 0; i < recs.length; i++) {
      var r = recs[i];
      if (!r || r.effect !== 'block') continue;
      if (!locationApplies(r, locationGid)) continue;
      if (!hireHitsRecord(pickUp, dropOff, r)) continue;
      if (Array.isArray(r.variants)) {
        for (var v = 0; v < r.variants.length; v++) out[idOf(r.variants[v])] = true;
      }
    }
    return out;
  }

  /*  The cascade. Which OPTION VALUES on `productGid` are unavailable because the
      gear behind them is blacked out.

      Returns [{ handle, name, optionName, values[], rule }] — the values rather than
      a boolean, because the caller has to grey the right radio button and the label
      is the only thing linking a mapping to a control on screen.

      Only mappings that name this product are considered. That is what makes the old
      add-on leak impossible rather than merely fixed: the previous Liquid channel sat
      outside the product test, so an override naming product X and add-on Y greyed Y
      on every product at that location.                                            */
  function blockedOptions(productGid, pickUp, dropOff, locationGid) {
    var res = [];
    var maps = window.csMappings;
    if (!Array.isArray(maps) || !maps.length || !productGid) return res;

    var blocked = blockedProductSet(pickUp, dropOff, locationGid);
    /*  No blocked gear, no cascade. Cheap exit before the nested loop, and the common
        case by a wide margin. */
    var any = false;
    for (var k in blocked) { any = true; break; }
    if (!any) return res;

    for (var i = 0; i < maps.length; i++) {
      var m = maps[i];
      if (!m || !Array.isArray(m.products)) continue;
      var onThis = false;
      for (var q = 0; q < m.products.length; q++) if (idOf(m.products[q]) === idOf(productGid)) { onThis = true; break; }
      if (!onThis) continue;
      var comps = Array.isArray(m.component_products) ? m.component_products : [];
      if (!comps.length) continue;

      var hits = 0;
      for (var c = 0; c < comps.length; c++) if (blocked[idOf(comps[c])]) hits++;
      if (hits === 0) continue;

      var rule = m.rule === 'block_if_all_out' ? 'block_if_all_out' : 'block_if_any_out';
      var out = rule === 'block_if_all_out' ? (hits === comps.length) : (hits > 0);
      if (!out) continue;

      res.push({
        handle: m.handle || '',
        name: m.name || '',
        optionName: m.option_name || '',
        values: Array.isArray(m.option_values) ? m.option_values.slice() : [],
        rule: rule
      });
    }
    return res;
  }

  /*  Is the PRODUCT ITSELF blacked out — named directly, not through a component.
      Kept separate from blockedOptions because the two mean different things to a
      customer and the surfaces treat them differently: this one takes the Add to
      booking button away, the other greys one choice inside it.                    */
  function productBlocked(productGid, pickUp, dropOff, locationGid) {
    if (!productGid) return false;
    return blockedProductSet(pickUp, dropOff, locationGid)[idOf(productGid)] === true;
  }

  function variantBlocked(variantGid, pickUp, dropOff, locationGid) {
    if (!variantGid) return false;
    return blockedVariantSet(pickUp, dropOff, locationGid)[idOf(variantGid)] === true;
  }

  /*  A package is NOT booked out because one option inside it is — confirmed
      22-09-2026. It is bookable with that choice unavailable. This returns the
      distinction so no caller has to infer it:

        blocked        the product itself is out. Refuse it.
        optionsOut     bookable, but these option values are gone.

      A caller that only wants a yes/no should read `.blocked`, never
      `.optionsOut.length` — conflating them is what would mark a whole pack
      unavailable because one uplight model is in for repair.                       */
  function resolve(productGid, pickUp, dropOff, locationGid) {
    return {
      blocked: productBlocked(productGid, pickUp, dropOff, locationGid),
      optionsOut: blockedOptions(productGid, pickUp, dropOff, locationGid)
    };
  }

  /*  Does a CHOSEN configuration carry a blocked option value.

      The cart needs this and the product page does not: on the product page the customer
      is still choosing, so the answer is "which choices are gone" (blockedOptions). In the
      cart the choice is made, so the answer is yes or no about this line.

      Values are compared verbatim. The mapping records hold every spelling an option value
      appears under, including repriced duplicates, precisely so no caller has to normalise
      — and normalising here would be a second opinion about what counts as the same option.
  */
  function configurationBlocked(productGid, chosenValues, pickUp, dropOff, locationGid) {
    if (!Array.isArray(chosenValues) || !chosenValues.length) return false;
    var outs = blockedOptions(productGid, pickUp, dropOff, locationGid);
    for (var i = 0; i < outs.length; i++) {
      var vals = outs[i].values;
      for (var v = 0; v < vals.length; v++) {
        if (chosenValues.indexOf(vals[v]) !== -1) return true;
      }
    }
    return false;
  }

  /*  ── ONE MARKER FOR A BOOKING ROW — dev-build 206 ──────────────────────────
      booking-row.liquid is shared by the cart page and the cart drawer, and both
      passed availability: 'available' — so every row said "Available" whatever the
      blackouts said. The drawer never checked at all; the cart page tinted the row
      but its badge still read "Available". Now both call this, with the same three
      tests the cart gate uses, and the badge text is booking-row.liquid's own.   */
  function markRows(root, pickUp, dropOff, locationGid) {
    var out = [];
    if (!root) return out;
    var rows = root.querySelectorAll('.bc-detail-body-gear-item[data-product-id]');
    for (var i = 0; i < rows.length; i++) {
      var el = rows[i];
      var pid = el.getAttribute('data-product-id');
      if (!pid) continue;
      var pg = 'gid://shopify/Product/' + pid;
      var vid = el.getAttribute('data-variant-id');
      var dated = !!(pickUp && dropOff);
      var blocked = false;
      if (dated) {
        blocked = productBlocked(pg, pickUp, dropOff, locationGid) ||
                  (!!vid && variantBlocked('gid://shopify/ProductVariant/' + vid, pickUp, dropOff, locationGid));
        if (!blocked) {
          var chosen = [];
          try { chosen = JSON.parse(el.getAttribute('data-option-values') || '[]'); } catch (e) { chosen = []; }
          if (Array.isArray(chosen) && chosen.length) blocked = configurationBlocked(pg, chosen, pickUp, dropOff, locationGid);
        }
      }
      el.classList.toggle('is-booked', blocked);
      var badge = el.querySelector('.bc-detail-body-gear-item-avail');
      if (badge && dated) {
        badge.classList.toggle('is-available', !blocked);
        badge.classList.toggle('is-unavailable', blocked);
        badge.classList.remove('is-pick');
        badge.title = blocked ? 'Booked out on some of these dates' : 'Available for your dates';
        badge.innerHTML = blocked ? 'Booked out' : '<span class="bc-live-dot" aria-hidden="true"></span>Available';
      }
      if (blocked) {
        var t = el.querySelector('.bc-detail-body-gear-item-title a') || el.querySelector('.bc-detail-body-gear-item-title');
        out.push({ el: el, title: t ? t.textContent.trim().replace(/^\d+\s*×\s*/, '') : '' });
      }
    }
    return out;
  }

  function locGidByName(name) {
    var l = null, all = window.locationMetaobjects || {};
    for (var k in all) if ((all[k].name || '') === name) { l = all[k]; break; }
    if (!l || !l.gid) return null;
    return String(l.gid).indexOf('gid://') === 0 ? String(l.gid) : 'gid://shopify/Metaobject/' + l.gid;
  }

  /*  The drawer's own saved booking, printed on .pb-cart-drawer-inner — never the
      bar's unsaved working dates. */
  function markDrawer(host) {
    var inner = (host || document).querySelector('.pb-cart-drawer-inner');
    if (!inner) return [];
    return markRows(inner, inner.getAttribute('data-pick') || '', inner.getAttribute('data-return') || '',
                    locGidByName(inner.getAttribute('data-location') || ''));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { markDrawer(document); });
  else markDrawer(document);

  window.DJWBlackout = {
    markRows: markRows,
    markDrawer: markDrawer,
    configurationBlocked: configurationBlocked,
    resolve: resolve,
    productBlocked: productBlocked,
    variantBlocked: variantBlocked,
    blockedOptions: blockedOptions,
    blockedProductSet: blockedProductSet,
    /*  Exposed for the cart, which needs the same night test against a record it
        already holds rather than re-deriving one. */
    hireHitsRecord: hireHitsRecord,
    locationApplies: locationApplies
  };
})();

/*  ═══════════════════════════════════════════════════════════════════════════
    dev-build 459 (Ben 15-10-2026) — LOW STOCK ON BOOKING ROWS
    The cart page and the cart drawer only ever marked booked-out rows. A row whose
    product is in a low_stock record for the booking's dates and location now shows an
    amber "Low stock" in place of "Available". Status only: the record's message stays
    on the product page. Booked-out rows are left to the block rule.
    Dates and location come from the cart itself (/cart.js), so the cart and drawer
    agree. Runs on load and whenever rows are re-rendered.
    ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  var B = window.DJWBlackout;
  if (!B || !B.locationApplies || !B.hireHitsRecord) return;
  var ctx = null, ctxAt = 0, busy = false;
  function locGid(name) {
    var all = window.locationMetaobjects; if (!all || !name) return null;
    var m = Object.keys(all).map(function (k) { return all[k]; }).find(function (l) { return (l.name || '') === name; });
    return m ? (String(m.gid).indexOf('gid://') === 0 ? String(m.gid) : 'gid://shopify/Metaobject/' + m.gid) : null;
  }
  function getCtx() {
    if (ctx && Date.now() - ctxAt < 1500) return Promise.resolve(ctx);
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) {
      var a = c.attributes || {};
      ctx = { start: a._pick_up_iso || '', end: a._return_iso || '', loc: locGid(a['Location'] || '') }; ctxAt = Date.now(); return ctx;
    });
  }
  function lowFor(pid, c) {
    var recs = window.csOverrides; if (!Array.isArray(recs) || !c.start || !c.end) return false;
    for (var i = 0; i < recs.length; i++) {
      var r = recs[i]; if (!r || r.effect !== 'low_stock') continue;
      if (!(r.products || []).some(function (p) { return String(p).split('/').pop() === String(pid); })) continue;
      if (!B.locationApplies(r, c.loc)) continue;
      if (!B.hireHitsRecord(c.start, c.end, r)) continue;
      return true;
    }
    return false;
  }
  function paint() {
    if (busy) return;
    var rows = document.querySelectorAll('.bc-detail-body-gear-item[data-product-id]'); if (!rows.length) return;
    var hasLow = Array.isArray(window.csOverrides) && window.csOverrides.some(function (r) { return r && r.effect === 'low_stock'; });
    if (!hasLow) { rows.forEach(function (row) { var av = row.querySelector('.bc-detail-body-gear-item-avail'); if (av) { av.classList.remove('is-low'); av.setAttribute('data-lowck', ''); } }); return; }
    busy = true;
    getCtx().then(function (c) {
      rows.forEach(function (row) {
        var av = row.querySelector('.bc-detail-body-gear-item-avail'); if (!av) return;
        /*  460: the label is drawn by CSS from .is-low, so a later rewrite of the row's
            text by the cart script cannot put "Available" back under the amber dot. */
        var low = !row.classList.contains('is-booked') && lowFor(row.dataset.productId, c);
        av.classList.toggle('is-low', low);
        if (low) av.title = 'Low stock';
        av.setAttribute('data-lowck', '');
      });
    }).catch(function () {}).then(function () { setTimeout(function () { busy = false; }, 0); });
  }
  var t = null; function soon() { clearTimeout(t); t = setTimeout(paint, 120); }
  document.addEventListener('DOMContentLoaded', soon);
  ['pbcart:updated', 'bookingrow:updated', 'bookingbar:changed', 'cart:updated'].forEach(function (e) { document.addEventListener(e, function () { ctx = null; soon(); }); });
  new MutationObserver(function (m) { for (var i = 0; i < m.length; i++) if (m[i].addedNodes.length) { if (document.querySelector('.bc-detail-body-gear-item-avail:not([data-lowck])')) paint(); else soon(); return; } }).observe(document.documentElement, { childList: true, subtree: true });
  soon();
})();
